import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Phone, Download, X, ChevronRight, Search, Voicemail } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../ui/sheet';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  analyzed: 'bg-emerald-100 text-emerald-700',
  escalated: 'bg-amber-100 text-amber-700',
  no_transcript_pending_retry: 'bg-rose-100 text-rose-700',
  no_transcript: 'bg-slate-100 text-slate-600',
  skipped_short: 'bg-slate-100 text-slate-500',
  pending: 'bg-indigo-100 text-indigo-700',
};

const _fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
};

const _fmtDuration = (secs) => {
  const n = Number(secs || 0);
  if (!n) return '—';
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
};

/**
 * Right-side slide-over that opens when a user clicks one of the six
 * outcome cards on /intelligence. Lists up to 200 calls behind that
 * outcome with the actual call status, agent, confidence, snippet, and
 * a jump-to-call link. Matches the same RBAC as the queue endpoint.
 */
export const OutcomeDrillSheet = ({
  open,
  onOpenChange,
  outcomeType,
  outcomeLabel,
  domainId,
  days = 60,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [hideVoicemail, setHideVoicemail] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !outcomeType) return;
    let cancelled = false;
    setLoading(true);
    const p = new URLSearchParams();
    p.set('outcome_type', outcomeType);
    p.set('days', String(days));
    p.set('limit', '200');
    if (domainId) p.set('domain_id', domainId);
    axios.get(`${API}/analytics/deal-outcomes/drill?${p.toString()}`)
      .then(r => { if (!cancelled) setRows(r.data?.rows || []); })
      .catch(() => { if (!cancelled) toast.error('Could not load drill-down.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, outcomeType, domainId, days]);

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams();
      if (outcomeType) p.set('outcome_type', outcomeType);
      p.set('days', String(days));
      p.set('limit', '2000');
      if (domainId) p.set('domain_id', domainId);
      const r = await axios.get(
        `${API}/analytics/deal-outcomes/export?${p.toString()}`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      link.setAttribute('download', m?.[1] || `deal-outcomes-${outcomeType || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch {
      toast.error('Export failed.');
    }
  };

  const countByStatus = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      const k = r.call_status || 'unknown';
      acc[k] = (acc[k] || 0) + 1;
    }
    return acc;
  }, [rows]);

  // Client-side keyword search + voicemail filter over the loaded rows.
  // Runs against agent name, phone, reason, and snippet — the four
  // fields a manager types keywords for ("Kylie", "0421…", "hesitant",
  // "gym membership").
  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (hideVoicemail && (r.is_voicemail || r.is_voicemail_screened)) return false;
      if (!needle) return true;
      const hay = [
        r.agent_name, r.customer_phone, r.reason_detected,
        r.supporting_transcript_snippet, r.recommended_next_action,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, hideVoicemail]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="outcome-drill-sheet"
      >
        <SheetHeader className="pb-4 border-b border-slate-100">
          <SheetTitle className="text-lg font-bold text-slate-900">
            {outcomeLabel || outcomeType} · Drill-down
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Last {days} days · {rows.length} call{rows.length === 1 ? '' : 's'}
            {Object.keys(countByStatus).length > 0 && (
              <span className="ml-1 text-slate-400">
                (
                {Object.entries(countByStatus).map(([k, n], i) => (
                  <span key={k}>
                    {i > 0 ? ' · ' : ''}
                    <span className="font-mono">{k}</span>: {n}
                  </span>
                ))}
                )
              </span>
            )}
          </SheetDescription>
          <div className="flex flex-wrap gap-2 items-center pt-2">
            <button
              onClick={exportCsv}
              disabled={loading || rows.length === 0}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-700 disabled:opacity-40 text-white inline-flex items-center gap-1.5"
              data-testid="drill-export-csv-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <label className="text-[11px] text-slate-600 inline-flex items-center gap-1 ml-auto">
              <input
                type="checkbox"
                checked={hideVoicemail}
                onChange={(e) => setHideVoicemail(e.target.checked)}
                className="rounded"
                data-testid="drill-hide-voicemail-toggle"
              />
              Hide voicemails
            </label>
          </div>
          <div className="pt-2 relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search snippet, agent, phone, reason…"
              className="w-full text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="drill-search-input"
            />
          </div>
        </SheetHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500">
            No calls matched this outcome in the selected window.
          </p>
        ) : filteredRows.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500">
            No matches for &ldquo;{q}&rdquo;{hideVoicemail ? ' (with voicemails hidden)' : ''}.
          </p>
        ) : (
          <>
            {q && (
              <p className="text-[11px] text-slate-500 pt-2 pb-1" data-testid="drill-search-count">
                Showing {filteredRows.length} of {rows.length} for &ldquo;{q}&rdquo;
              </p>
            )}
            <ul className="divide-y divide-slate-100 pt-2">
              {filteredRows.map((r) => {
              const badge = STATUS_BADGE[r.call_status] || 'bg-slate-100 text-slate-600';
              return (
                <li
                  key={r.call_id}
                  className="py-3 hover:bg-slate-50 -mx-6 px-6 cursor-pointer group"
                  onClick={() => navigate(`/calls/${r.call_id}`)}
                  data-testid={`drill-row-${r.call_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge}`}>
                          {r.call_status || 'unknown'}
                        </span>
                        {(r.is_voicemail || r.is_voicemail_screened) && (
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 inline-flex items-center gap-0.5"
                            title={r.is_voicemail_screened ? 'Voicemail screened (hit customer VM)' : 'Voicemail greeting'}
                            data-testid={`drill-voicemail-${r.call_id}`}
                          >
                            <Voicemail className="w-3 h-3" />
                            {r.is_voicemail_screened ? 'VM-scr' : 'VM'}
                          </span>
                        )}
                        <span className="text-xs text-slate-500 tabular-nums">
                          {_fmtDate(r.call_date)}
                        </span>
                        <span className="text-xs text-slate-400">·</span>
                        <span className="text-xs text-slate-500 tabular-nums">
                          {_fmtDuration(r.duration_seconds)}
                        </span>
                        {typeof r.confidence_score === 'number' && (
                          <>
                            <span className="text-xs text-slate-400">·</span>
                            <span className="text-xs font-semibold text-slate-700">
                              {r.confidence_score}% conf
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-700 mb-1">
                        <span className="font-semibold">
                          {r.agent_name || r.agent_id || '—'}
                        </span>
                        {r.customer_phone && (
                          <span className="text-slate-500 inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />{r.customer_phone}
                          </span>
                        )}
                      </div>
                      {r.supporting_transcript_snippet && (
                        <p className="text-[11px] italic text-slate-500 line-clamp-2 mt-1">
                          “{r.supporting_transcript_snippet}”
                        </p>
                      )}
                      {r.reason_detected && (
                        <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">
                          <span className="font-semibold">Why: </span>
                          {r.reason_detected}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-1" />
                  </div>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OutcomeDrillSheet;
